/**
 * Pure helpers for HaynesPro work-order time adoption (#1442).
 * Server remains authoritative for hours; the client never invents a zero.
 */

import type {
  HaynesProCarTypeGroup,
  HaynesProPreviewResult,
  HaynesProRepairTreeNode,
  HaynesProTimeSnapshot,
  HaynesProWorkOrderTimeCapability,
} from '@/hooks/useHaynesProWorkOrderTime';

export const MAX_REPAIR_PATH_DEPTH = 8;

export function isHaynesProSnapshotsEnabled(
  capability: HaynesProWorkOrderTimeCapability | undefined,
): boolean {
  return capability?.snapshotsEnabled === true;
}

export function needsConfirmedVariant(
  capability: HaynesProWorkOrderTimeCapability | undefined,
): boolean {
  if (!capability) return true;
  const variant = capability.confirmedVariant;
  return !variant || variant.usable !== true || capability.isStale === true;
}

/** Finite, non-negative decimal hours. Explicit 0 is valid; missing/NaN is not. */
export function isAuthoritativeDecimalHours(hours: unknown): hours is number {
  return typeof hours === 'number' && Number.isFinite(hours) && hours >= 0;
}

/**
 * Provider hours are integer hundredths (125 → 1.25 h). Explicit 0 is valid.
 * Null, NaN, Infinity, negatives, and mismatched conversions are not.
 */
export function hoursMatchHundredths(laborHours: unknown, hundredths: unknown): boolean {
  if (!isAuthoritativeDecimalHours(laborHours)) return false;
  if (typeof hundredths !== 'number' || !Number.isFinite(hundredths)) return false;
  if (!Number.isInteger(hundredths) || !Number.isSafeInteger(hundredths) || hundredths < 0) {
    return false;
  }
  return Math.round(laborHours * 100) === hundredths && laborHours === hundredths / 100;
}

export function hasAuthoritativeGuideTime(
  preview: Pick<HaynesProPreviewResult, 'laborHours' | 'hundredths'> | null,
): boolean {
  if (!preview) return false;
  return hoursMatchHundredths(preview.laborHours, preview.hundredths);
}

export function isDifferentWorkposition(
  snapshot: HaynesProTimeSnapshot | null | undefined,
  workpositionId: string | undefined,
): boolean {
  return Boolean(
    snapshot && workpositionId && snapshot.workpositionId !== workpositionId,
  );
}

/** Active snapshot already mapped onto a different labor step, including the same position. */
export function isMappedToOtherLaborItem(
  snapshot: HaynesProTimeSnapshot | null | undefined,
  laborItemId: string | undefined,
): boolean {
  return Boolean(
    snapshot?.laborItemId && laborItemId && snapshot.laborItemId !== laborItemId,
  );
}

export function isHaynesProOverlapBlocked(
  snapshot: HaynesProTimeSnapshot | null | undefined,
  workpositionId: string | undefined,
  laborItemId: string | undefined,
): boolean {
  return (
    isMappedToOtherLaborItem(snapshot, laborItemId) ||
    isDifferentWorkposition(snapshot, workpositionId)
  );
}

export function formatHaynesProHours(hours: number): string {
  if (!Number.isFinite(hours)) return '';
  return Number(hours.toFixed(2)).toString();
}

export function formatHaynesProMinutes(minutes: number): string {
  if (!Number.isFinite(minutes)) return '';
  return Number.isInteger(minutes)
    ? String(minutes)
    : Number(minutes.toFixed(2)).toString();
}

type AdoptedLaborProvenance = {
  id: string;
  estimatedHours?: number;
  timeSource?: string | null;
  estimatedHoursOriginal?: number | null;
  editedBy?: string | null;
  editedAt?: string | null;
  editedByName?: string | null;
};

/**
 * Merge only authoritative hours/provenance into the matching row.
 * Description, discount, sort order, and sibling drafts stay local.
 */
export function applyAdoptedLaborItem<T extends { id: string; clientKey: string }>(
  prevItems: readonly T[],
  adopted: AdoptedLaborProvenance,
): T[] {
  if (!isAuthoritativeDecimalHours(adopted.estimatedHours)) {
    return prevItems.slice();
  }
  const hours = adopted.estimatedHours;
  return prevItems.map((item) => {
    if (item.id !== adopted.id) return item;
    return {
      ...item,
      estimatedHours: hours,
      timeSource: 'timeSource' in adopted ? adopted.timeSource : 'provider',
      estimatedHoursOriginal:
        'estimatedHoursOriginal' in adopted ? adopted.estimatedHoursOriginal : hours,
      editedBy: 'editedBy' in adopted ? adopted.editedBy : null,
      editedAt: 'editedAt' in adopted ? adopted.editedAt : null,
      editedByName: 'editedByName' in adopted ? adopted.editedByName : null,
      clientKey: item.clientKey,
    };
  });
}

export function canEnterGroup(
  ancestorsLength: number,
  node: HaynesProRepairTreeNode,
): boolean {
  if (!node.hasSubnodes) return false;
  // Path includes the leaf, so ancestors may grow to MAX-1.
  return ancestorsLength < MAX_REPAIR_PATH_DEPTH - 1;
}

export function previewPathForLeaf(
  ancestorIds: readonly string[],
  leafId: string,
): string[] {
  return [...ancestorIds, leafId];
}

export function isSelectableLeaf(node: HaynesProRepairTreeNode): boolean {
  return (
    node.selectable === true &&
    node.hasSubnodes === false &&
    node.unsupportedReason == null &&
    node.hundredths != null &&
    Number.isFinite(node.hundredths)
  );
}

export const DEFAULT_CAR_TYPE_GROUP: HaynesProCarTypeGroup = 'ENGINE';
